// openEHR LANG model — BMM package org.openehr.lang.beom.core
// Generated by tasks/split_model_by_bmm_packages.ts from tasks/bmm_package_map.json.
// Package boundaries follow the BMM package structure; edit the class bodies here,
// but re-run the splitter rather than hand-moving declarations between packages.

import type { EXPR_TYPE_DEF } from "./types.ts";
import * as openehr_base from "../../base/mod.ts";

/**
 * Meta-type for the notion of statement, which is a non-value-returning entity.
 */
export abstract class STATEMENT {
}

/**
 * Meta-type for a first order predicate logic expression with a Boolean result.
 */
export class ASSERTION extends STATEMENT {
  /**
   * Internal storage for tag
   * @protected
   */
  protected _tag?: openehr_base.String;

  /**
   * Expression tag, used for differentiating multiple assertions.
   */
  get tag(): string | undefined {
    return this._tag?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for tag.
   * Use this to access openehr_base.String methods.
   */
  get $tag(): openehr_base.String | undefined {
    return this._tag;
  }

  /**
   * Sets tag from either a primitive value or openehr_base.String wrapper.
   */
  set tag(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._tag = undefined;
    } else if (typeof val === "string") {
      this._tag = openehr_base.String.from(val);
    } else {
      this._tag = val;
    }
  }

  /**
   * Internal storage for string_expression
   * @protected
   */
  protected _string_expression?: openehr_base.String;

  /**
   * String form of expression, in case an expression evaluator taking String expressions is used for evaluation.
   */
  get string_expression(): string | undefined {
    return this._string_expression?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for string_expression.
   * Use this to access openehr_base.String methods.
   */
  get $string_expression(): openehr_base.String | undefined {
    return this._string_expression;
  }

  /**
   * Sets string_expression from either a primitive value or openehr_base.String wrapper.
   */
  set string_expression(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._string_expression = undefined;
    } else if (typeof val === "string") {
      this._string_expression = openehr_base.String.from(val);
    } else {
      this._string_expression = val;
    }
  }

  /**
   * Root of expression tree.
   */
  expression?: EXPRESSION;
}

/**
 * Meta-type representing the assignment statement, which associates a named variable with an expression, and produces no value.
 */
export class ASSIGNMENT extends STATEMENT {
  /**
   * The target variable on the notional left-hand side of this assignment.
   */
  target?: VARIABLE_DECLARATION;
  /**
   * Source right hand side) of the assignment.
   */
  source?: EXPR_VALUE;
}

/**
 * Any kind of statement element that can be evaluated. The type will either be supplied in descendant types or else will be inferred by an assignment statement linked to a typed variable.
 */
export abstract class EXPR_VALUE {
  /**
   * The computed value of this node as a result of the nodes below it, for operator nodes, or else statically set or otherwise derived values.
   * @returns Result value
   */
  value(): openehr_base.Any {
    throw new Error("Method value not yet implemented.");
  }
}

/**
 * Abstract parent of all expression meta-types.
 */
export abstract class EXPRESSION extends EXPR_VALUE {
  /**
   * The primitive type of this node, which must be determined by redefinitions in concrete classes.
   * @returns Result value
   */
  type(): EXPR_TYPE_DEF {
    throw new Error("Method type not yet implemented.");
  }
}

/**
 * Abstract parent of operator types.
 */
export abstract class EXPR_OPERATOR extends EXPRESSION {
  /**
   * Internal storage for precedence_overridden
   * @protected
   */
  protected _precedence_overridden?: openehr_base.Boolean;

  /**
   * True if the natural precedence of operators is overridden in the expression represented by this node of the expression tree. If True, parentheses should be introduced around the totality of the syntax expression corresponding to this operator node and its operands.
   */
  get precedence_overridden(): boolean | undefined {
    return this._precedence_overridden?.value;
  }

  /**
   * Gets the openehr_base.Boolean wrapper object for precedence_overridden.
   * Use this to access openehr_base.Boolean methods.
   */
  get $precedence_overridden(): openehr_base.Boolean | undefined {
    return this._precedence_overridden;
  }

  /**
   * Sets precedence_overridden from either a primitive value or openehr_base.Boolean wrapper.
   */
  set precedence_overridden(val: boolean | openehr_base.Boolean | undefined) {
    if (val === undefined || val === null) {
      this._precedence_overridden = undefined;
    } else if (typeof val === "boolean") {
      this._precedence_overridden = openehr_base.Boolean.from(val);
    } else {
      this._precedence_overridden = val;
    }
  }

  /**
   * Operator definition.
   */
  operator?: OPERATOR_KIND;
  /**
   * Internal storage for symbol
   * @protected
   */
  protected _symbol?: openehr_base.String;

  /**
   * The symbol actually used in the rule, or intended to be used for serialisation. Must be a member of \`operator_def.symbols\`.
   */
  get symbol(): string | undefined {
    return this._symbol?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for symbol.
   * Use this to access openehr_base.String methods.
   */
  get $symbol(): openehr_base.String | undefined {
    return this._symbol;
  }

  /**
   * Sets symbol from either a primitive value or openehr_base.String wrapper.
   */
  set symbol(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._symbol = undefined;
    } else if (typeof val === "string") {
      this._symbol = openehr_base.String.from(val);
    } else {
      this._symbol = val;
    }
  }
}

/**
 * Binary operator expression node.
 */
export class EXPR_BINARY_OPERATOR extends EXPR_OPERATOR {
  /**
   * Left operand node.
   */
  left_operand?: EXPRESSION;
  /**
   * Right operand node.
   */
  right_operand?: EXPRESSION;
}

/**
 * Meta-type representing one of:
 *
 * * a manifest constant of any primitive type;
 * * a path referring to a value in the archetype;
 * * a constraint;
 * * a variable reference.
 */
export abstract class EXPR_LEAF extends EXPRESSION {
  /**
   * The reference item from which the value of this node can be computed.
   */
  item?: openehr_base.Any | VARIABLE_DECLARATION;
}

/**
 * Node representing a function call with 0 or more arguments.
 */
export class EXPR_FUNCTION_CALL extends EXPR_LEAF {
  /**
   * Arguments of this function, which can be from 0 to any number. Functions with no arguments are typically used to represent real world varying values like 'current time' and so on.
   */
  arguments?: undefined;
}

/**
 * Literal value expression tree leaf item. This can represent a literal value of any primitive type included in the \`PRIMITIVE_TYPE\` enumeration.
 */
export class EXPR_LITERAL extends EXPR_LEAF {
  /**
   * A statically set constant value of a primitive type.
   */
  declare item?: openehr_base.Any;
}

/**
 * Unary operator expression node.
 */
export class EXPR_UNARY_OPERATOR extends EXPR_OPERATOR {
  /**
   * Operand node.
   */
  operand?: EXPRESSION;
}

/**
 * Expression tree leaf item representing a reference to a declared variable.
 */
export class EXPR_VARIABLE_REF extends EXPR_LEAF {
  /**
   * The variable referred to.
   */
  override item?: VARIABLE_DECLARATION = undefined;
}

/**
 * Definition of a variable whose value is derived from a query run on a data context in the operational environment. Typical uses of this kind of variable are to obtain values like the patient date of birth, sex, weight, and so on. It could also be used to obtain items from a knowledge context, such as a drug database.
 */
export class EXTERNAL_QUERY extends EXPR_VALUE {
  /**
   * Internal storage for context
   * @protected
   */
  protected _context?: openehr_base.String;

  /**
   * Optional name of context. This allows a basic separation of query types to be done in more sophisticated environments. Possible values might be “patient”, “medications” and so on.
   * Not yet standardised.
   */
  get context(): string | undefined {
    return this._context?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for context.
   * Use this to access openehr_base.String methods.
   */
  get $context(): openehr_base.String | undefined {
    return this._context;
  }

  /**
   * Sets context from either a primitive value or openehr_base.String wrapper.
   */
  set context(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._context = undefined;
    } else if (typeof val === "string") {
      this._context = openehr_base.String.from(val);
    } else {
      this._context = val;
    }
  }

  /**
   * Internal storage for query_id
   * @protected
   */
  protected _query_id?: openehr_base.String;

  /**
   * Identifier of query in the external context, e.g. “date_of_birth”.
   * Not yet standardised.
   */
  get query_id(): string | undefined {
    return this._query_id?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for query_id.
   * Use this to access openehr_base.String methods.
   */
  get $query_id(): openehr_base.String | undefined {
    return this._query_id;
  }

  /**
   * Sets query_id from either a primitive value or openehr_base.String wrapper.
   */
  set query_id(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._query_id = undefined;
    } else if (typeof val === "string") {
      this._query_id = openehr_base.String.from(val);
    } else {
      this._query_id = val;
    }
  }

  /**
   * Optional arguments to query.
   * Not yet standardised.
   */
  query_args?: undefined;
}

/**
 * A container for a specific set of statements intended to be used together.
 */
export class STATEMENT_SET {
  /**
   * The member statements of this statement set.
   */
  statement?: undefined;
  /**
   * Internal storage for name
   * @protected
   */
  protected _name?: openehr_base.String;

  /**
   * Optional name of this rule set.
   */
  get name(): string | undefined {
    return this._name?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for name.
   * Use this to access openehr_base.String methods.
   */
  get $name(): openehr_base.String | undefined {
    return this._name;
  }

  /**
   * Sets name from either a primitive value or openehr_base.String wrapper.
   */
  set name(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._name = undefined;
    } else if (typeof val === "string") {
      this._name = openehr_base.String.from(val);
    } else {
      this._name = val;
    }
  }

  /**
   * Execution result of the whole rule set. Determined by the and-ing of result values of Assertions in the rule set.
   * @returns Result value
   */
  execution_result(): openehr_base.Boolean {
    // Return True by default (all assertions passed)
    // In a real implementation, would evaluate all statements in the set
    // and return false if any assertion failed
    if (!this.statement) {
      return openehr_base.Boolean.from(true);
    }
    // Return true as default - actual implementation would evaluate statements
    return openehr_base.Boolean.from(true);
  }
}

/**
 * Meta-type for the declaration of a named variable that can be used in an expression.
 */
export class VARIABLE_DECLARATION extends STATEMENT {
  /**
   * Internal storage for name
   * @protected
   */
  protected _name?: openehr_base.String;

  /**
   * Name of the variable.
   */
  get name(): string | undefined {
    return this._name?.value;
  }

  /**
   * Gets the openehr_base.String wrapper object for name.
   * Use this to access openehr_base.String methods.
   */
  get $name(): openehr_base.String | undefined {
    return this._name;
  }

  /**
   * Sets name from either a primitive value or openehr_base.String wrapper.
   */
  set name(val: string | openehr_base.String | undefined) {
    if (val === undefined || val === null) {
      this._name = undefined;
    } else if (typeof val === "string") {
      this._name = openehr_base.String.from(val);
    } else {
      this._name = val;
    }
  }

  /**
   * Primitive type of the variable, enabling its use to be type-checked in expressions.
   */
  type?: EXPR_TYPE_DEF;
}

/**
 * Path-based reference to a value in a data structure.
 */
export class EXPR_VALUE_REF extends EXPR_LEAF {
}

/**
 * Universal quantification operator, usually known as \`for_all\`, whose operand is a collection of items referenced by an \`EXPR_VALUE_REF\`. The \`_condition_\` attribute represents an assertion that is applied to every member of the collection at runtime to determine the result.
 */
export class EXPR_FOR_ALL extends EXPR_OPERATOR {
  /**
   * Boolean condition that returns True or False when applied to a member of the operand of a \`for_all\` operator.
   */
  condition?: ASSERTION;
  /**
   * Reference to collection / container object to which the \`for_all\` operator is to be applied.
   */
  operand?: EXPR_VALUE_REF;
}

/**
 * Enumeration representing operators.
 */
export class OPERATOR_KIND extends openehr_base.String {
}
